/**
 * ReferrerDashboard
 * Main dashboard for referrers showing their referrals and ability to submit new ones
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  PlusCircle, LogOut, FileText, Clock, CheckCircle, 
  Calendar, AlertCircle, RefreshCw, User, ChevronRight
} from 'lucide-react';
import API_BASE_URL from '../config/api';
import { useReferrerPortalStatus } from '../utils/referrerPortalStatus';
import ReferralsDisabledNotice from '../components/ReferralsDisabledNotice';

const STATUS_CONFIG = {
  submitted: { label: 'Received', color: '#f0ad4e', icon: Clock, bg: '#fff8e1' },
  processing: { label: 'Processing', color: '#5bc0de', icon: RefreshCw, bg: '#e1f5fe' },
  form_sent: { label: 'Form Sent to Patient', color: '#6b9bd1', icon: FileText, bg: '#e3f2fd' },
  form_completed: { label: 'Form Completed', color: '#5cb85c', icon: CheckCircle, bg: '#e8f5e9' },
  booked: { label: 'Appointment Booked', color: '#5cb85c', icon: Calendar, bg: '#e8f5e9' },
  completed: { label: 'Completed', color: '#999', icon: CheckCircle, bg: '#f5f5f5' },
};

function ReferrerDashboard() {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const referrer = JSON.parse(sessionStorage.getItem('referrer') || '{}');
  const token = sessionStorage.getItem('referrerToken');
  const { loading: statusLoading, submissionsEnabled, notice } = useReferrerPortalStatus();

  useEffect(() => {
    if (!token) {
      navigate('/referrer/login');
      return;
    }
    fetchReferrals();
  }, []);

  const fetchReferrals = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/referrer-referrals/my-referrals`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.status === 401) {
        sessionStorage.removeItem('referrerToken');
        sessionStorage.removeItem('referrer');
        navigate('/referrer/login');
        return;
      }

      const data = await res.json();
      if (data.success) {
        setReferrals(data.referrals);
      }
    } catch (err) {
      setError('Unable to load referrals');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('referrerToken');
    sessionStorage.removeItem('referrer');
    navigate('/referrer/login');
  };

  const getStatusConfig = (status) => {
    return STATUS_CONFIG[status] || STATUS_CONFIG.submitted;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-NZ', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const getInjuryLabel = (site, side) => {
    const sideLabel = side ? ` (${side.charAt(0).toUpperCase() + side.slice(1)})` : '';
    const siteLabels = {
      KNEE: 'Knee', HIP: 'Hip', SHOULDER: 'Shoulder', ANKLE: 'Ankle',
      FOOT: 'Foot', ELBOW: 'Elbow', WRIST: 'Wrist/Hand',
      LUMBAR_SPINE: 'Lumbar Spine', CERVICAL_SPINE: 'Cervical Spine',
      THORACIC_SPINE: 'Thoracic Spine', ACHILLES: 'Achilles',
      HAMSTRING: 'Hamstring', CONCUSSION: 'Concussion'
    };
    return (siteLabels[site] || site) + sideLabel;
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f4f8' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #6b9bd1 0%, #4a7fb5 100%)',
        padding: '15px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}>
        <div>
          <h1 style={{ color: 'white', margin: 0, fontSize: 20, fontWeight: 700 }}>
            ClinHx Referrer Portal
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.8)', margin: '4px 0 0', fontSize: 13 }}>
            {referrer.name} — {referrer.practiceName}
          </p>
        </div>
        <button
          onClick={handleLogout}
          style={{
            backgroundColor: 'rgba(255,255,255,0.15)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.3)',
            padding: '8px 16px',
            borderRadius: 6,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 13,
          }}
        >
          <LogOut size={14} /> Sign Out
        </button>
      </div>

      <div style={{ maxWidth: 800, margin: '0 auto', padding: 20 }}>
        {/* New Referral — replaced by the notice while submissions are off.
            Nothing is rendered until the status is known, so the button never
            flashes up only to disappear. */}
        {statusLoading ? null : submissionsEnabled ? (
          <button
            onClick={() => navigate('/referrer/new')}
            style={{
              width: '100%',
              padding: '16px 20px',
              backgroundColor: '#e8941c',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              marginBottom: 25,
              boxShadow: '0 2px 8px rgba(232,148,28,0.3)',
            }}
          >
            <PlusCircle size={20} /> New Referral
          </button>
        ) : (
          <ReferralsDisabledNotice notice={notice} style={{ marginBottom: 25 }} />
        )}

        {/* Referrals List */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 15 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#333' }}>Your Referrals</h2>
          <button
            onClick={fetchReferrals}
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #ddd',
              borderRadius: 6,
              padding: '6px 12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 13,
              color: '#666',
            }}
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {error && (
          <div style={{
            backgroundColor: '#fee', border: '1px solid #fcc', color: '#c33',
            padding: '12px 15px', borderRadius: 6, marginBottom: 15, fontSize: 14,
          }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            Loading referrals...
          </div>
        ) : referrals.length === 0 ? (
          <div style={{
            textAlign: 'center', padding: 40,
            backgroundColor: 'white', borderRadius: 8,
            border: '1px solid #eee',
          }}>
            <FileText size={48} color="#ddd" style={{ marginBottom: 15 }} />
            <p style={{ color: '#999', fontSize: 16, marginBottom: 5 }}>No referrals yet</p>
            <p style={{ color: '#bbb', fontSize: 14 }}>
              {submissionsEnabled
                ? 'Click "New Referral" to submit your first referral'
                : 'Referrals sent to reception will not appear here'}
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {referrals.map(referral => {
              const statusConfig = getStatusConfig(referral.status);
              const StatusIcon = statusConfig.icon;

              return (
                <div
                  key={referral.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: 8,
                    border: '1px solid #eee',
                    padding: '15px 20px',
                    cursor: 'pointer',
                    transition: 'box-shadow 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 15,
                  }}
                  onClick={() => navigate(`/referrer/referral/${referral.id}`)}
                  onMouseEnter={e => e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)'}
                  onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
                >
                  {/* Patient Icon */}
                  <div style={{
                    width: 44, height: 44, borderRadius: '50%',
                    backgroundColor: '#f0f4f8',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <User size={20} color="#6b9bd1" />
                  </div>

                  {/* Details */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: '#333', marginBottom: 3 }}>
                      {referral.patientName}
                    </div>
                    <div style={{ fontSize: 13, color: '#888' }}>
                      {getInjuryLabel(referral.injurySite, referral.injurySide)}
                      {referral.accNumber && ` • ACC: ${referral.accNumber}`}
                    </div>
                    <div style={{ fontSize: 12, color: '#aaa', marginTop: 3 }}>
                      Submitted {formatDate(referral.createdAt)}
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div style={{
                    backgroundColor: statusConfig.bg,
                    color: statusConfig.color,
                    padding: '5px 10px',
                    borderRadius: 20,
                    fontSize: 12,
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}>
                    <StatusIcon size={12} />
                    {statusConfig.label}
                  </div>

                  <ChevronRight size={16} color="#ccc" style={{ flexShrink: 0 }} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default ReferrerDashboard;
